import React from 'react';
import { Paper, Box, Typography, Divider, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface UpdateResult {
  ok: boolean;
  error?: string;
  added: string[];
  changed: string[];
  deleted: string[];
  rebuilt: string[];
  ingested: string[];
  rag: { changed: number; deleted: number; embedded: number } | null;
}

const pr = new Intl.PluralRules('ru');
function plural(n: number, one: string, few: string, many: string) {
  const r = pr.select(n);
  return r === 'one' ? one : r === 'few' ? few : many;
}

export default function Report({ data }: { data: UpdateResult }) {
  if (!data.ok) {
    return <Alert severity="error">Ошибка: {data.error ?? 'неизвестная'}</Alert>;
  }

  const noChanges = !data.added.length && !data.changed.length && !data.deleted.length;

  const rows: [string, number, string][] = [
    ['Добавлено',        data.added.length,   plural(data.added.length,   'страница','страницы','страниц')],
    ['Изменено',         data.changed.length,  plural(data.changed.length, 'страница','страницы','страниц')],
    ['Удалено',          data.deleted.length,  plural(data.deleted.length, 'страница','страницы','страниц')],
    ['Пересобрано',      data.rebuilt.length,  plural(data.rebuilt.length, 'раздел','раздела','разделов')],
    ['Отправлено в RAG', data.ingested.length, plural(data.ingested.length,'статья','статьи','статей')],
  ];

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
        {noChanges
          ? <Typography variant="body2" color="text.secondary">Изменений нет</Typography>
          : rows.map(([label, n, unit]) => n === 0 ? null : (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{n} {unit}</Typography>
              </Box>
            ))}
        {data.rag && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Эмбединги созданы</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }} color="success.main">{data.rag.embedded} шт.</Typography>
            </Box>
          </>
        )}
      </Paper>
      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="caption" color="text.secondary">Подробно (JSON)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', m: 0 }}>
            {JSON.stringify(data, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
